import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import type React from "react";

import { cn } from "../../lib/utils";

const tooltipStyles = {
  positioner:
    "z-floating isolate h-(--positioner-height) w-(--positioner-width) max-w-(--available-width) data-[anchor-hidden]:invisible",
  popup:
    "relative flex h-(--popup-height,auto) w-(--popup-width,auto) items-center gap-1.5 origin-(--transform-origin) data-[side=top]:origin-bottom data-[side=bottom]:origin-top data-[side=left]:origin-right data-[side=inline-start]:origin-right data-[side=right]:origin-left data-[side=inline-end]:origin-left rounded-md border border-border-secondary bg-fg bg-clip-padding px-2 py-1 text-xs text-bg shadow-L1 outline-none transition-[transform,opacity] duration-75 will-change-transform data-ending-style:scale-[.96] data-starting-style:scale-[.96] data-ending-style:opacity-0 data-starting-style:opacity-0 data-instant:duration-0",
  content:
    "relative size-full overflow-clip data-instant:transition-none **:data-current:data-ending-style:opacity-0 **:data-current:data-starting-style:opacity-0 **:data-previous:data-ending-style:opacity-0 **:data-previous:data-starting-style:opacity-0 **:data-previous:truncate **:data-current:opacity-100 **:data-previous:opacity-100 **:data-current:transition-opacity **:data-previous:transition-opacity",
};

const TooltipCreateHandle: typeof TooltipPrimitive.createHandle =
  TooltipPrimitive.createHandle;

function TooltipProvider({
  delay = 0,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delay}
      {...props}
    />
  );
}

function Tooltip({ ...props }: TooltipPrimitive.Root.Props) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger(
  props: TooltipPrimitive.Trigger.Props,
): React.ReactElement {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipPopup({
  className,
  side = "top",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  anchor,
  children,
  portalProps,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    "align" | "alignOffset" | "anchor" | "side" | "sideOffset"
  > & {
    portalProps?: TooltipPrimitive.Portal.Props;
  }): React.ReactElement {
  return (
    <TooltipPrimitive.Portal {...portalProps}>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        side={side}
        sideOffset={sideOffset}
        data-slot="tooltip-positioner"
        className={tooltipStyles.positioner}
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-popup"
          className={cn(tooltipStyles.popup, className)}
          {...props}
        >
          <TooltipPrimitive.Viewport
            data-slot="tooltip-viewport"
            className={tooltipStyles.content}
          >
            {children}
          </TooltipPrimitive.Viewport>
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

const TooltipContent = TooltipPopup;

export {
  Tooltip,
  TooltipTrigger,
  TooltipPopup,
  TooltipContent,
  TooltipProvider,
  TooltipCreateHandle,
};
